import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function TaskOverview() {
  const navigate = useNavigate();

  return (
     <ProtectedLayout>
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">The Power of Taking Action</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8 text-xl">
              Every time you take action — no matter how big or small — you are moving closer to your goals.
            </p>

            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <p className="text-gray-700 mb-0">
                Inside the Core Victory Method, action isn't random or busywork. It's intentional. Every task you complete comes from answering the CVM questions — which means your actions are grounded in your goals and aligned with your core values.
              </p>
            </div>

            <p className="text-gray-600 mb-6">
              This changes how action works.
            </p>

            <p className="text-gray-600 mb-8">
              Most people live in a world where tasks are just items to check off a to-do list. But inside this app — inside this method — every task you complete represents clarity, courage, and connection to what matters most.
            </p>

            <p className="text-gray-600 mb-8">
              When you answer your daily MAS questions, you are taking the time to reflect on what you want, what's working, what's in the way, and what your best self would do next. That process transforms ordinary actions into meaningful steps toward becoming the person you want to be.
            </p>

            <div className="bg-green-50 p-6 rounded-lg mb-8">
              <h2 className="text-2xl font-semibold text-[#004060] mb-4">This is why every action you take is worthy of celebration.</h2>
              <div className="grid grid-cols-2 gap-4 text-gray-700">
                <div>
                  <ul className="list-none p-0 m-0 space-y-2">
                    <li>• Sending the email</li>
                    <li>• Making the call</li>
                    <li>• Taking a walk</li>
                    <li>• Speaking up for yourself</li>
                  </ul>
                </div>
                <div>
                  <ul className="list-none p-0 m-0 space-y-2">
                    <li>• Finishing a project</li>
                    <li>• Resting when needed</li>
                    <li>• Asking for help</li>
                    <li>• Trying again after failure</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-gray-600 mb-8">
              These aren't just tasks — they are evidence. Proof that you are showing up for your life. Proof that you are becoming the kind of person who follows through on what matters.
            </p>

            <div className="bg-purple-50 p-6 rounded-lg mb-8">
              <p className="text-gray-700 mb-4">
                Research shows that completing tasks and checking them off gives your brain a hit of dopamine — creating energy, momentum, and motivation for the next step. (BJ Fogg, Tiny Habits; Teresa Amabile, The Progress Principle)
              </p>
              <p className="text-gray-700 mb-0">
                But this app takes it one step further.
              </p>
            </div>

            <p className="text-gray-600 mb-8">
              Because you've done the work to clarify your values and reflect on your goals, every action you take here carries extra meaning. You're not just doing more. You're doing what matters. You're doing what aligns with your best self.
            </p>

            <p className="text-gray-600 mb-8">
              That's why small actions lead to big results.
            </p>

            <p className="text-gray-600 mb-8">
              Over time, these small, consistent, values-aligned actions stack up. They create new habits. They create confidence. They create change.
            </p>

            <div className="bg-orange-50 p-6 rounded-lg mb-8">
              <p className="text-gray-700 mb-4">
                This is how you get ahead — not just in your business, or your health, or your relationships — but in your whole life.
              </p>
              <div className="space-y-2 text-gray-700 font-medium">
                <p>One clear step at a time.</p>
                <p>One aligned action at a time.</p>
                <p>One celebration at a time.</p>
              </div>
            </div>

            <p className="text-gray-600 mb-8">
              You are building proof that you can trust yourself. Proof that you take action on what matters. Proof that you can move forward no matter what.
            </p>

            <div className="bg-gradient-to-r from-[#004060] to-[#236caf] text-white p-8 rounded-lg">
              <p className="text-xl font-medium mb-0">
                And that's always worth celebrating.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
     </ProtectedLayout>
  );
}